/**
 * ListRow — icon + title + subtitle + right-accessory row for inset lists.
 * Design source: prototype.jsx:382-414 (InsetRow).
 * Atom purity: imports only react + tokens.
 */
import { CSSProperties, ReactNode, useState } from 'react';
import { P, S, T } from '../../styles/tokens';

export interface ListRowProps {
  /** Leading icon element (e.g. an <Ic> wrapped in a tile). */
  icon?: ReactNode;
  /** Background color of the icon tile. */
  iconBg?: string;
  /** Primary text label. */
  title: string;
  /** Secondary text below the title. */
  subtitle?: string;
  /** Trailing accessory slot (e.g. a chevron or value label). */
  right?: ReactNode;
  /** Click handler — when set, row becomes interactive (pointer cursor + press dim). */
  onClick?: () => void;
  /** When true the bottom hairline separator is hidden (use on last row in a group). */
  isLast?: boolean;
  style?: CSSProperties;
}

export function ListRow({
  icon,
  iconBg = P.terracotta,
  title,
  subtitle,
  right,
  onClick,
  isLast = false,
  style,
}: ListRowProps) {
  const [pressed, setPressed] = useState(false);

  const interactive = !!onClick;

  const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 52,
    // prototype uses 8px vertical, 16px horizontal (382-405)
    padding: '8px 16px',
    boxSizing: 'border-box',
    cursor: interactive ? 'pointer' : 'default',
    // press state: bg wash instead of opacity dim (prototype:403)
    background: interactive && pressed ? 'rgba(0,0,0,0.04)' : 'transparent',
    transition: 'background 0.12s ease',
    position: 'relative',
    ...style,
  };

  const iconTileStyle: CSSProperties = {
    width: 30,
    height: 30,
    // prototype uses borderRadius: 7 (prototype:408)
    borderRadius: 7,
    background: iconBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#ffffff',
  };

  const textBlockStyle: CSSProperties = {
    flex: 1,
    minWidth: 0,
  };

  const titleStyle: CSSProperties = {
    // prototype:414 uses T.body (17px/400) — mapped to T.bodyText in tokens.ts
    font: T.bodyText,
    color: S.label,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const subtitleStyle: CSSProperties = {
    font: T.footnote,
    color: S.secondaryLabel,
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const separatorStyle: CSSProperties = {
    position: 'absolute',
    bottom: 0,
    // prototype:432-434: left=58 when icon present (16 padding + 30 tile + 12 gap), else 16
    left: icon !== undefined ? 58 : 16,
    right: 0,
    height: 0.5,
    background: S.separator,
    pointerEvents: 'none',
  };

  return (
    <div
      style={rowStyle}
      onClick={interactive ? onClick : undefined}
      onPointerDown={interactive ? () => setPressed(true) : undefined}
      onPointerUp={interactive ? () => setPressed(false) : undefined}
      onPointerLeave={interactive ? () => setPressed(false) : undefined}
    >
      {icon !== undefined && (
        <div style={iconTileStyle}>{icon}</div>
      )}
      <div style={textBlockStyle}>
        <div style={titleStyle}>{title}</div>
        {subtitle !== undefined && (
          <div style={subtitleStyle}>{subtitle}</div>
        )}
      </div>
      {right !== undefined && (
        <div style={{ flexShrink: 0, color: S.secondaryLabel }}>{right}</div>
      )}
      {!isLast && <div style={separatorStyle} />}
    </div>
  );
}
